import bdjs from "@bd-core/bdjs";
import $ from "jquery";
import {
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    stopTimerFromPause,
} from "@bd-core/firebase-actions";
import bdapi from "@bd-core/bd-api";
import CONFIG from "@bd-core/config";
import WaveSurfer from "wavesurfer.js";
import QRCode from "qrcode";
import { formatTime } from "../utils/formatTime";

export const TimerPage = () => {
    const isTimerPage = $("#timer-page").length > 0;
    if (!isTimerPage) return false;

    let startTime = null;
    let accumulatedTime = 0;
    let isPaused = false;
    let isFinished = false;
    let sessionId = null;
    let waveformAudio = null;
    let isAudioSession = false;
    let isVideoSession = false;
    let isAudioReady = false;
    let isVideoReady = false;
    let allMediaReady = false;
    const updateInterval = 1000;
    const maxSceneDuration = 2 * 60 * 60;
    let timerIntervalId = null;
    let isSyncingVideoAndTimer = false;
    let videoFrames = [];
    let frameCapturCanvas = null;
    let frameCaptureCtx = null;
    let lastFrameCaptureTime = 0;
    const FRAME_CAPTURE_INTERVAL = 2; // seconds - capture frame every 2 seconds
    let videoDuration = 0;

    // Get media URLs from data attributes
    const $timerPage = $("#timer-page");
    const audioUrl = $timerPage.data("audio-url");
    const videoUrl = $timerPage.data("video-url");
    const mediaType = $timerPage.data("media-type");
    
    const ensureTeamCodeInUrl = () => {
        if (!CONFIG.firebase?.team_code) return;
        
        const urlParams = new URLSearchParams(window.location.search);
        const urlTeamCode = urlParams.get('team_code') || urlParams.get('team-code');
        const configTeamCode = CONFIG.firebase.team_code.toLowerCase();
        
        if (!urlTeamCode || urlTeamCode.toLowerCase() !== configTeamCode) {
            const newUrl = `${CONFIG.siteURL}/timer/?team_code=${configTeamCode}`;
            window.history.replaceState({}, '', newUrl);
        }
    };
    
    ensureTeamCodeInUrl();
   

    /**
     * Captures a frame from the video element
     * @param {HTMLVideoElement} videoElement
     * @returns {string|null} Base64 encoded image or null
     */
    const captureVideoFrame = (videoElement) => {
        try {
            if (!frameCapturCanvas) {
                frameCapturCanvas = document.createElement('canvas');
                frameCaptureCtx = frameCapturCanvas.getContext('2d');
            }
            
            frameCapturCanvas.width = videoElement.videoWidth || 640;
            frameCapturCanvas.height = videoElement.videoHeight || 360;
            
            frameCaptureCtx.drawImage(videoElement, 0, 0, frameCapturCanvas.width, frameCapturCanvas.height);
            
            return frameCapturCanvas.toDataURL('image/jpeg', 0.8);
        } catch (error) {
            console.error('Error capturing video frame:', error);
            return null;
        }
    };
    
    /**
     * Saves captured video frames to the backend
     * @returns {Promise<void>}
     */
    const saveVideoFrames = async () => {
        if (!sessionId || videoFrames.length === 0) {
            console.warn('Cannot save video frames - missing sessionId or no frames captured');
            return;
        }
        
        try {
            const [response] = await bdjs.goAsync(
                bdapi.post("game/save-video-frames", {
                    session_id: sessionId,
                    frames: videoFrames,
                    video_duration: videoDuration
                })
            );
            
            if (response?.data?.success === false) {
                console.error('Backend returned error:', response.data);
            }
        } catch (error) {
            console.error('Error saving video frames:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        }
    };
    
    /**
     * Monitors video playback and captures frames at intervals
     * @param {HTMLVideoElement} videoElement
     * @returns {void}
     */
    const monitorVideoForFrames = (videoElement) => {
        if (!videoElement || !isVideoSession) return;
        
        const checkAndCaptureFrame = () => {
            if (!videoElement.paused && !videoElement.ended) {
                const currentTime = videoElement.currentTime;
                
                if (currentTime - lastFrameCaptureTime >= FRAME_CAPTURE_INTERVAL) {
                    const frame = captureVideoFrame(videoElement);
                    if (frame) {
                        videoFrames.push({
                            timestamp: currentTime,
                            data: frame
                        });
                        lastFrameCaptureTime = currentTime;
                    } else {
                        console.warn(`Failed to capture frame at ${currentTime}s`);
                    }
                }
            }
        };
        
        videoElement.addEventListener('timeupdate', checkAndCaptureFrame);
    };

    /**
     * Checks if all required media is ready and enables the start button
     * @returns {void}
     */
    const checkMediaReady = () => {
        let mediaReady = true;
        
        if (isAudioSession && !isAudioReady) {
            mediaReady = false;
        }
        
        if (isVideoSession && !isVideoReady) {
            mediaReady = false;
        }
        
        if (mediaReady && !allMediaReady) {
            allMediaReady = true;
            enableStartButton();
        }
    };
    
    /**
     * Enables the start button once all media is loaded
     * @returns {void}
     */
    const enableStartButton = () => {
        const $startButton = $(".js-timer-button-right.is-start");
        $startButton.removeClass("is-loading").attr("aria-disabled", "false");
    };
    
    /**
     * Disables the start button and shows loading state
     * @returns {void}
     */
    const disableStartButton = () => {
        const $startButton = $(".js-timer-button-right.is-start");
        $startButton.addClass("is-loading").attr("aria-disabled", "true");
        allMediaReady = false;
    };

    /**
     * Updates the timer display DOM elements with formatted time
     * @param {number} timeInSeconds
     * @returns {void}
     */
    const updateTimerDisplay = (timeInSeconds) => {
        const timerElement = $(".timer__module__time");
        const formattedTime = formatTime(timeInSeconds);
        const [minutes, seconds] = formattedTime.split(":");

        timerElement.find(".minutes").text(minutes);
        timerElement.find(".seconds").text(`:${seconds}`);
    };

    /**
     * Ends the current scene/timer session
     * Calculates final accumulated time, stops the timer, and redirects to results page
     * @returns {Promise<void>}
     */
    const endScene = async () => {
        if (isPaused) {
            stopTimerFromPause();
        } else {
            accumulatedTime = accumulatedTime + (Date.now() - startTime) / 1000;
            stopTimer(accumulatedTime);
        }

        $(".timer__module").removeClass("has-started has-paused");
        $(".js-timer-button-right")
            .removeClass("is-stop")
            .addClass("is-start is-loading")
            .attr("aria-disabled", "true");
        $(".js-timer-button-right")
            .find(".timer__module__action__text")
            .text(CONFIG.page_settings.timer.button_label.start);
        
        if (isVideoSession && videoFrames.length > 0) {
            await saveVideoFrames();
        } else {
            console.warn('endScene - NOT saving video frames:', {
                isVideoSession,
                videoFramesLength: videoFrames.length,
                reason: !isVideoSession ? 'Not a video session' : 'No frames captured'
            });
        }
        
        allMediaReady = false;
        isAudioReady = false;
        isVideoReady = false;
        
        await bdjs.goAsync(
            bdapi.post("game/finish-game", {
                accumulated_time: accumulatedTime,
            }),
        );

        const activeSessionId = CONFIG.firebase.active_session_id || sessionId;
        window.location.href = `/result?team-code=${CONFIG.firebase.team_code}&session-id=${activeSessionId}`;
    };

    /**
     * Starts the timer interval that updates the display every second
     * @returns {void}
     */
    const startTimerInterval = () => {
        if (timerIntervalId) {
            clearInterval(timerIntervalId);
        }
        
        const updateTimerTick = () => {
            if (isFinished || isPaused || !startTime) return;
            
            const elapsedTime = accumulatedTime + (Date.now() - startTime) / 1000;
            
            if (elapsedTime >= maxSceneDuration) {
                endScene();
                return;
            }
            
            updateTimerDisplay(elapsedTime);
        };
        
        updateTimerTick();
        
        timerIntervalId = setInterval(updateTimerTick, updateInterval);
    };

    /**
     * Stops the currently running timer interval
     * @returns {void}
     */
    const stopTimerInterval = () => {
        if (timerIntervalId) {
            clearInterval(timerIntervalId);
            timerIntervalId = null;
        }
    };

    /**
     * Automatically starts or stops the interval based on pause state
     * @param {Object} options
     * @returns {void}
     */
    const setTimeState = ({ 
        newStartTime = null, 
        newAccumulatedTime = null, 
        newIsPaused = null 
    } = {}) => {
        if (newStartTime !== null) {
            startTime = newStartTime;
        }
        
        if (newAccumulatedTime !== null) {
            accumulatedTime = typeof newAccumulatedTime === 'number' && !isNaN(newAccumulatedTime) 
                ? newAccumulatedTime 
                : 0;
        }
        
        if (newIsPaused !== null) {
            isPaused = newIsPaused;
        }

        updateTimerDisplay(accumulatedTime);

        if (!isPaused && startTime) {
            startTimerInterval();
        } else {
            stopTimerInterval();
        }
    };

    const syncVideoWithTimer = (elapsedTimeInSeconds) => {
        if (!isVideoSession) return;
        
        const videoElement = $("#timer-video").get(0);
        if (!videoElement || !(videoElement instanceof HTMLVideoElement)) {
            return;
        }
        
        if (!videoDuration || videoDuration === 0) {
            videoElement.addEventListener('loadedmetadata', () => {
                videoDuration = videoElement.duration;
                syncVideoWithTimer(elapsedTimeInSeconds);
            }, { once: true });
            return;
        }
        
        let targetTime = elapsedTimeInSeconds;
        
        if (targetTime > videoDuration) {
            targetTime = videoDuration - 0.1;
        }
        
        isSyncingVideoAndTimer = true;
        videoElement.currentTime = targetTime;
        
        setTimeout(() => {
            isSyncingVideoAndTimer = false;
        }, 200);
    };

    /**
     * Initializes session state when page first loads
     * @param {Event} e
     * @param {Object} eData
     * @returns {Promise<void>}
     */
    const setSessionInitialState =  async (e, eData) => {
        const activeSessionId = CONFIG.firebase.active_session_id || false;
        const data = eData?.data?.data || eData?.data;

        const teamCode = $(".timer__link__text a").data("team-code") || CONFIG.firebase.team_code;
        const joinUrl = teamCode 
            ? `${CONFIG.siteURL}/join/?team_code=${teamCode.toLowerCase()}`
            : CONFIG.siteURL;
        
        QRCode.toString(joinUrl, { type: "svg" }, (err, svg) => {
            if (!err) {
                $(".js-qrcode").html(svg);
                $(".qrcode-popup__code").html(svg);
            }
        });

        const allSessionItems = $(".sessions-popup__item");
        const selectableSessions = allSessionItems.not(".is-finished");
        const hasSessionsToSelect = activeSessionId || selectableSessions.length > 0 || allSessionItems.length > 0;
        const hasNoSessionData = !data || data === null || (typeof data === 'object' && data.data === null);
        const allSessionsFinished = allSessionItems.length > 0 && selectableSessions.length === 0;
        
        const urlParams = new URLSearchParams(window.location.search);
        const isReplayMode = urlParams.get('replay') === '1';
        
        if (hasNoSessionData) {
            if (hasSessionsToSelect) {
                bdjs.hideLoading();
                
                if (isReplayMode && selectableSessions.length === 0 && allSessionItems.length > 0) {
                    allSessionItems.removeClass("is-finished").removeAttr("disabled").removeAttr("data-finished");
                }
                
                if (isReplayMode) {
                    loadUserSessions();
                } else {
                    
                    const sessionItems = selectableSessions.length > 0 ? selectableSessions : allSessionItems;
                    if (sessionItems.length === 1) {
                        $(".sessions-popup__body").addClass("is-one-row");
                    }
                }

                $(".sessions-popup").addClass("is-open");

                if(allSessionsFinished) {
                    $(".sessions-popup").removeClass("is-open");
                    $(".start-popup").addClass("is-open");
                }
                
                startTime = null;
                accumulatedTime = 0;
                isPaused = false;
                isFinished = false;
                sessionId = null;
                
                setTimeState({
                    newStartTime: null,
                    newAccumulatedTime: 0,
                    newIsPaused: false
                });
                return;
            }
            
            bdjs.hideLoading();
            startTime = null;
            accumulatedTime = 0;
            isPaused = false;
            isFinished = false;
            sessionId = null;
            
            setTimeState({
                newStartTime: null,
                newAccumulatedTime: 0,
                newIsPaused: false
            });
            
            if (allSessionsFinished) {
                $(".start-popup").addClass("is-open");
                loadUserSessions();
            }
            return;
        }

        bdjs.hideLoading();

        isAudioSession = mediaType === 'audio' && audioUrl;
        isVideoSession = mediaType === 'video' && videoUrl;
        
        if (!isAudioSession && !isVideoSession) {
            enableStartButton();
        } else {
            disableStartButton();
        }

        if (isAudioSession && audioUrl) {
            const audioContainer = $(".timer__media--audio");
            
            if (audioContainer.length > 0) {
                try {
                    waveformAudio = WaveSurfer.create({
                        container: ".timer__media--audio",
                        url: audioUrl,
                        waveColor: CONFIG.color_scheme.primary_color ?? "#4F4A85",
                        progressColor: CONFIG.color_scheme.secondary_color ?? "#383351",
                        barWidth: 2,
                        barGap: 1,
                        barRadius: 2,
                        normalize: true,
                        interact: false,
                    });

                    waveformAudio.on('ready', () => {
                        isAudioReady = true;
                        checkMediaReady();
                    });

                    waveformAudio.on('error', (error) => {
                        console.error('Audio loading error:', error);
                        isAudioReady = true;
                        checkMediaReady();
                    });
                } catch (error) {
                    console.error('WaveSurfer initialization error:', error);
                    isAudioReady = true;
                    checkMediaReady();
                }
            } else {
                console.warn('Audio container not found in DOM');
                isAudioReady = true;
                checkMediaReady();
            }
        } else {
            isAudioReady = true;
        }

        if (isVideoSession && videoUrl) {
            const $video = $("#timer-video");
            
            if ($video.length > 0) {
                const videoElement = $video.get(0);
                
                if (videoElement && videoElement instanceof HTMLVideoElement) {
                    let canPlayThroughFired = false;
                    const MIN_BUFFER_PERCENT = 20;
                    const LOADING_TIMEOUT = 30000;
                    let loadingTimeout = null;
                    
                    const preventVideoPlayback = (e) => {
                        if (videoElement.hasAttribute('data-playback-disabled')) {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            if (!videoElement.paused) {
                                videoElement.pause();
                            }
                            
                            alert("Please wait for the video is playable - please maintain a good network service");
                            return false;
                        }
                    };
                    
                    $video.on('play playing', preventVideoPlayback);
                    
                    const getBufferedPercent = () => {
                        if (!videoElement.duration) return 0;
                        
                        const buffered = videoElement.buffered;
                        if (buffered.length === 0) return 0;
                        
                        const bufferedEnd = buffered.end(buffered.length - 1);
                        const duration = videoElement.duration;
                        const percentBuffered = (bufferedEnd / duration) * 100;
                        
                        return percentBuffered;
                    };
                    
                    const updateLoadingProgress = () => {
                        const percent = getBufferedPercent();
                        if (percent > 0 && percent < 100) {
                            $(".timer__module__action__loading-text").text(`Loading video... ${Math.floor(percent)}%`);
                        }
                    };
                    
                    const enableVideoPlayback = () => {
                        if (isVideoReady) return; 
                        
                        $(".timer__module__action__loading-text").text('Video ready!');
                        
                        videoElement.removeAttribute('data-playback-disabled');
                        
                        $video.off('play playing', preventVideoPlayback);
                        
                        if (loadingTimeout) {
                            clearTimeout(loadingTimeout);
                            loadingTimeout = null;
                        }
                        
                        isVideoReady = true;
                        checkMediaReady();
                    };
                    
                    const checkVideoReady = () => {
                        if (isVideoReady) return;
                        
                        const bufferedPercent = getBufferedPercent();
                        
                        if (canPlayThroughFired && bufferedPercent >= MIN_BUFFER_PERCENT) {
                            enableVideoPlayback();
                        }
                    };
                    
                    $video.on('progress', () => {
                        updateLoadingProgress();
                        checkVideoReady();
                    });
                    
                    $video.on('canplaythrough', () => {
                        canPlayThroughFired = true;
                        checkVideoReady();
                    });
                    
                    videoElement.addEventListener('loadedmetadata', () => {
                        videoDuration = videoElement.duration;
                    });
                    
                    $video.on('error', () => {
                        console.error('Video loading error');
                        isVideoReady = true;
                        checkMediaReady();
                    });
                    
                    monitorVideoForFrames(videoElement);
                    
                    videoElement.load();
                    
                    const triggerBuffering = async () => {
                        try {
                            isSyncingVideoAndTimer = true;
                            
                            const playPromise = videoElement.play();
                            
                            if (playPromise !== undefined) {
                                await playPromise;
                                videoElement.pause();
                                videoElement.currentTime = 0;
                            }
                            
                            isSyncingVideoAndTimer = false;
                        } catch (error) {
                            isSyncingVideoAndTimer = false;
                        }
                    };
                    
                    setTimeout(triggerBuffering, 500);
                    
                    loadingTimeout = setTimeout(() => {
                        console.warn('Video loading timeout reached, enabling anyway...');
                        if (!isVideoReady) {
                            $(".timer__module__action__loading-text").text('Ready to start');
                            enableVideoPlayback();
                        }
                    }, LOADING_TIMEOUT);
                    
                    $video.on('play', function() {
                        if (isSyncingVideoAndTimer) return;
                        
                        if (!videoElement.hasAttribute('data-playback-disabled') && isVideoReady) {
                            const $startButton = $(".js-timer-button-right.is-start");
                            
                            if ($startButton.length > 0 && !$startButton.hasClass("is-loading")) {
                                isSyncingVideoAndTimer = true;
                                $startButton.trigger('click');
                                setTimeout(() => { isSyncingVideoAndTimer = false; }, 100);
                            }
                        }
                    });
                    
                    $video.on('pause waiting', function() {
                        if (isSyncingVideoAndTimer) return;
                        
                        if (!videoElement.hasAttribute('data-playback-disabled') && isVideoReady && startTime) {
                            const $stopButton = $(".js-timer-button-right.is-stop");
                            const $pauseButton = $(".js-timer-button-left.is-pause");
                            
                            if ($pauseButton.length > 0 && !isPaused) {
                                isSyncingVideoAndTimer = true;
                                $pauseButton.trigger('click');
                                setTimeout(() => { isSyncingVideoAndTimer = false; }, 100);
                            }
                            
                            else if ($stopButton.length > 0 && isPaused) {
                                if (!videoElement.paused) {
                                    videoElement.pause();
                                }
                            }
                        }
                    });
                }
            } else {
                console.warn('Video element not found in DOM');
                isVideoReady = true;
                checkMediaReady();
            }
        } else {
            isVideoReady = true;
        }
        
        checkMediaReady();

        startTime = data.startTime;
        accumulatedTime = typeof data.accumulatedTime === 'number' && !isNaN(data.accumulatedTime) 
            ? data.accumulatedTime 
            : 0;
        isPaused = data.isPaused;
        isFinished = data.isFinished;
        sessionId = data.sessionId;

        if (!sessionId && activeSessionId) {
            $(".sessions-popup").addClass("is-open");

            const sessionItems = $(".sessions-popup__item");
            if (sessionItems.length === 1) {
                $(".sessions-popup__body").addClass("is-one-row");
            }
        }

        setTimeState({
            newStartTime: startTime,
            newAccumulatedTime: accumulatedTime,
            newIsPaused: isPaused
        });

        if (startTime && isVideoSession) {
            let currentElapsedTime = accumulatedTime;
            
            if (!isPaused && startTime) {
                currentElapsedTime = accumulatedTime + (Date.now() - startTime) / 1000;
            }
            
            setTimeout(() => {
                syncVideoWithTimer(currentElapsedTime);
            }, 500);
        }

        if (isPaused) {
            $(".timer__module").addClass("has-paused");
            $(".js-timer-button-left")
                .removeClass("is-pause")
                .addClass("is-resume");
            $(".js-timer-button-left")
                .find(".timer__module__action__text")
                .text(CONFIG.page_settings.timer.button_label.resume);
            $(".js-timer-button-right")
                .removeClass("is-start is-loading")
                .addClass("is-stop")
                .attr("aria-disabled", "false");
            $(".js-timer-button-right")
                .find(".timer__module__action__text")
                .text(CONFIG.page_settings.timer.button_label.stop);
        } else if (startTime) {
            $(".timer__module").addClass("has-started");
            $(".js-timer-button-right")
                .removeClass("is-start is-loading")
                .addClass("is-stop")
                .attr("aria-disabled", "false");
            $(".js-timer-button-right")
                .find(".timer__module__action__text")
                .text(CONFIG.page_settings.timer.button_label.stop);
        }
    };

    /**
     * Handles real-time session data changes from Firebase
     * @param {Event} e - Event object
     * @param {Object} eData - Event data containing updated session information
     * @param {Object} eData.data - Updated session data object
     * @returns {void}
     */
    const setSessionDataChanged = (e, eData) => {
        const data = eData?.data?.data || eData?.data;

        if (!data) return;

        const previousSessionId = sessionId;
        
        isFinished = data.isFinished;
        sessionId = data.sessionId;
        
        setTimeState({
            newStartTime: data.startTime,
            newAccumulatedTime: data.accumulatedTime,
            newIsPaused: data.isPaused
        });
        
        if (!previousSessionId && sessionId) {
            $(".sessions-popup").removeClass("is-open");
        }
    };

    bdjs.listen("bd__sessionInitialState", setSessionInitialState);
    bdjs.listen("bd__sessionDataChanged", setSessionDataChanged);

    $(document).on("click", ".js-timer-button-right.is-start", async function(e) {
        const $button = $(this);
        
        if ($button.hasClass("is-loading") || $button.attr("aria-disabled") === "true") {
            e.preventDefault();
            e.stopPropagation();
            alert("Please wait for the video is playable - please maintain a good network service");
            return false;
        }
        
        if (!sessionId) return;
        
        if (!allMediaReady) {
            console.warn('Start button clicked but media not ready yet');
            alert("Please wait for the video is playable - please maintain a good network service");
            return;
        }

        videoFrames = [];
        lastFrameCaptureTime = 0;

        const currentTime = Date.now();
        
        setTimeState({
            newStartTime: currentTime,
            newAccumulatedTime: accumulatedTime,
            newIsPaused: false
        });

        startTimer(currentTime);
        $(".timer__module")
            .removeClass("has-paused has-started")
            .addClass("has-started");
        $(".js-timer-button-right").removeClass("is-start").addClass("is-stop");
        $(".js-timer-button-right")
            .find(".timer__module__action__text")
            .text(CONFIG.page_settings.timer.button_label.stop);
        $(".js-timer-button-left")
            .removeClass("is-resume")
            .addClass("is-pause");
        if (isAudioSession) {
            $(".timer__content").addClass("is-audio-visible");
        }
        if (isVideoSession) {
            $(".timer__content").addClass("is-video-visible");
        }

        await bdjs.goAsync(bdapi.post("game/start-game", {
            session_id: sessionId,
        }));

        if (waveformAudio && isAudioReady) {
            waveformAudio.play();
        }
        if (isVideoSession) {
            const videoElement = $("#timer-video").get(0);
            if (videoElement && videoElement instanceof HTMLVideoElement) {
                isSyncingVideoAndTimer = true;
                videoElement.play();
                setTimeout(() => { isSyncingVideoAndTimer = false; }, 100);
                
                // Capture first frame immediately
                const firstFrame = captureVideoFrame(videoElement);
                if (firstFrame) {
                    videoFrames.push({
                        timestamp: 0,
                        data: firstFrame
                    });
                } else {
                    console.warn('Failed to capture first video frame at start');
                }
            }
        }
    });

    $(document).on(
        "click",
        ".js-timer-button-left.is-pause, .js-timer-button-right.is-stop",
        () => {
            const newAccumulatedTime = isPaused 
                ? accumulatedTime 
                : accumulatedTime + (Date.now() - startTime) / 1000 || 0;

            setTimeState({
                newStartTime: startTime,
                newAccumulatedTime: newAccumulatedTime,
                newIsPaused: true
            });

            pauseTimer(newAccumulatedTime);

            if (waveformAudio && isAudioReady) {
                waveformAudio.pause();
            }

            if (isVideoSession) {
                const videoElement = $("#timer-video").get(0);
                if (videoElement && videoElement instanceof HTMLVideoElement) {
                    isSyncingVideoAndTimer = true;
                    videoElement.pause();
                    setTimeout(() => { isSyncingVideoAndTimer = false; }, 100);
                }
            }

            $(".timer__module")
                .removeClass("has-started has-paused")
                .addClass("has-paused");
            $(".js-timer-button-left")
                .removeClass("is-pause")
                .addClass("is-resume");
            $(".js-timer-button-left")
                .find(".timer__module__action__text")
                .text(CONFIG.page_settings.timer.button_label.resume);
        },
    );

    $(document).on(
        "click",
        ".js-timer-button-left.is-resume, .js-popup-cancel",
        () => {
            const currentTime = Date.now();

            setTimeState({
                newStartTime: currentTime,
                newAccumulatedTime: accumulatedTime,
                newIsPaused: false
            });

            resumeTimer(currentTime);
            $(".timer__module")
                .removeClass("has-started has-paused")
                .addClass("has-started");
            $(".js-timer-button-left")
                .removeClass("is-resume")
                .addClass("is-pause");
            $(".js-timer-button-left")
                .find(".timer__module__action__text")
                .text(CONFIG.page_settings.timer.button_label.pause);

            if (waveformAudio && isAudioReady && !isPaused) {
                waveformAudio.play();
            }
            if (isVideoSession && !isPaused) {
                const videoElement = $("#timer-video").get(0);
                if (videoElement && videoElement instanceof HTMLVideoElement) {
                    syncVideoWithTimer(accumulatedTime);
                    
                    setTimeout(() => {
                        isSyncingVideoAndTimer = true;
                        videoElement.play();
                        setTimeout(() => { isSyncingVideoAndTimer = false; }, 100);
                    }, 300);
                }
            }
        },
    );

    $(document).on("click", ".js-end-scene", async () => {
        await endScene();
    });

    $(document).on("click", ".js-timer-button-right.is-stop", () => {
        $(".timer").addClass("has-dialog");
    });

    $(document).on("click", ".js-popup-cancel", () => {
        $(".timer").removeClass("has-dialog");
    });

    $(document).on("click", ".sessions-popup__item", async (e) => {
        const $button = $(e.currentTarget);
        
        if ($button.hasClass("is-finished") || $button.data("finished")) {
            return;
        }
        
        const selectedSessionId = $button.data("session-id");
        if (!selectedSessionId) return;

        const teamCode = CONFIG.firebase?.team_code;
        if (!teamCode) {
            return;
        }

        $(".sessions-popup").addClass("is-loading");
        const [response] = await bdjs.goAsync(
            bdapi.post("game/select-session", {
                session_id: selectedSessionId,
                team_code: teamCode,
            }),
        );

        if (response.data.success) {
            $(".sessions-popup").removeClass("is-open is-loading");
            
            const newActiveSessionId = response.data.data.active_session_id;
            if (newActiveSessionId) {
                CONFIG.firebase.active_session_id = newActiveSessionId;
                const reloadUrl = `${CONFIG.siteURL}/timer/?team_code=${teamCode.toLowerCase()}`;
                window.location.href = reloadUrl;
            }
        } else {
            $(".sessions-popup").removeClass("is-loading");
        }
    });

    $(document).on("click", ".js-qrcode", () => {
        $(".qrcode-popup").addClass("is-open");
    });

    $(document).on("click", ".qrcode-popup__close", () => {
        $(".qrcode-popup").removeClass("is-open");
    });

    const loadUserSessions = async () => {
        const [response] = await bdjs.goAsync(
            bdapi.get("game/get-user-game-progress")
        );

        if (response.data.success) {
            const { game_progress_list } = response.data.data;
            const $sessionsList = $(".js-sessions-list");
            
            if (game_progress_list.length === 0) {
                $sessionsList.html('<p class="start-popup__empty">' + 
                    'No sessions found. Create a new one to get started!' + 
                    '</p>');
                return;
            }

            let sessionsHtml = '';
            game_progress_list.forEach(gp => {
                const isActive = gp.is_active;
                const statusClass = gp.is_finished ? 'is-finished' : 
                                  gp.is_started ? 'is-started' : 'not-started';
                const statusText = gp.is_finished ? 'Finished' : 'In Progress';
                
                sessionsHtml += `
                    <div class="start-popup__session-item ${statusClass} ${isActive ? 'is-active' : ''}" 
                         data-game-progress-id="${gp.id}">
                        <div class="start-popup__session-info">
                            <span class="start-popup__session-code">${gp.team_code.toUpperCase()}</span>
                            <span class="start-popup__session-status">${statusText}</span>
                            ${isActive ? '<span class="start-popup__session-badge">Active</span>' : ''}
                        </div>
                        ${!isActive ? `<button class="start-popup__session-switch btn btn--small js-switch-session" 
                                              data-game-progress-id="${gp.id}">
                            Switch to this session
                        </button>` : ''}
                    </div>
                `;
            });
            
            $sessionsList.html(sessionsHtml);
        } else {
            $(".js-sessions-list").html('<p class="start-popup__error">Failed to load sessions</p>');
        }
    };

    $(document).on("click", ".js-go-to-previous-session", () => {
        const urlParams = new URLSearchParams(window.location.search);
        const isReplayMode = urlParams.get('replay') === '1';
        
        $(".start-popup").removeClass("is-open");
        
        if (isReplayMode) {
            $(".sessions-popup").addClass("is-open");
            
            const allSessionItems = $(".sessions-popup__item");
            const sessionItems = allSessionItems.not(".is-finished");
            const itemsToCheck = sessionItems.length > 0 ? sessionItems : allSessionItems;
            
            if (itemsToCheck.length === 1) {
                $(".sessions-popup__body").addClass("is-one-row");
            }
        }
    });

    $(document).on("click", ".js-choose-another-scenario", () => {
        if (!isPaused && startTime) {
            $(".js-timer-button-right.is-stop").trigger("click");
        }
        
        $(".start-popup").removeClass("is-open");
        $(".sessions-popup").addClass("is-open");
    });

    $(document).on("click", ".js-start-new-session", async () => {
        bdjs.showLoading();
        
        const [response] = await bdjs.goAsync(
            bdapi.post("game/create-new-game-session")
        );

        if (response.data.success) {
            const redirectUrl = response.data.data.redirect;
            window.location.href = redirectUrl;
        } else {
            bdjs.hideLoading();
            alert('Failed to create new session. Please try again.');
        }
    });

    $(document).on("click", ".js-switch-session", async (e) => {
        const $button = $(e.currentTarget);
        const gameProgressId = $button.data("game-progress-id");
        
        if (!gameProgressId) return;

        bdjs.showLoading();
        
        const [response] = await bdjs.goAsync(
            bdapi.post("game/switch-game-progress", {
                game_progress_id: gameProgressId
            })
        );

        if (response.data.success) {
            const redirectUrl = response.data.data.redirect;
            window.location.href = redirectUrl;
        } else {
            bdjs.hideLoading();
            alert(response.data.message || 'Failed to switch session. Please try again.');
        }
    });

    const checkStartPopupAndLoadSessions = () => {
        if ($(".start-popup").hasClass("is-open")) {
            loadUserSessions();
        }
    };

    setTimeout(checkStartPopupAndLoadSessions, 500);

    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class') {
                const target = $(mutation.target);
                if (target.hasClass('start-popup') && target.hasClass('is-open')) {
                    loadUserSessions();
                }
            }
        });
    });

    const startPopupElement = document.querySelector('.start-popup');
    if (startPopupElement) {
        observer.observe(startPopupElement, { attributes: true });
    }

    $(document).on("click", ".js-open-session-management", () => {
        $(".start-popup").addClass("is-open");
        loadUserSessions();
    });

    $(document).on("click", ".js-close-start-popup", () => {
        $(".start-popup").removeClass("is-open");
    });
};